import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePageParams } from '@/App';
import { setCanonical, setMeta } from '@/utils/seo';
import { INDUSTRY_MAP, INDUSTRIES } from '@/data/industries';
import LeadForm from '@/components/LeadForm';
import { ServiceSchema, FAQSchema, BreadcrumbSchema, SpeakableSchema } from '@/components/SchemaMarkup';
import { ChevronDown, ChevronUp, MapPin, AlertTriangle, DollarSign, Lightbulb } from 'lucide-react';
import HeroBackground from '@/components/HeroBackground';
import GoogleReviews from '@/components/GoogleReviews';
import api from '@/utils/api';

const INDUSTRY_IMAGES = {
  construction: '/images/construction.jpg',
  restaurants: '/images/restaurants.jpg',
  'home-health': '/images/home-health.jpg',
  landscaping: '/images/landscaping.jpg',
  trucking: '/images/trucking.jpg',
  'all-businesses': '/images/all-businesses.jpg'
};

export default function CityPage() {
  const { citySlug, industrySlug } = usePageParams();
  const [city, setCity] = useState(null);
  const [authority, setAuthority] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tip, setTip] = useState('');
  const [paa, setPaa] = useState([]);

  const ind = industrySlug ? INDUSTRY_MAP[industrySlug] : null;
  const indName = ind ? ind.name : 'Businesses';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const cityRes = await api.get(`/cities/${citySlug}`);
        setCity(cityRes.data);
        const cityName = cityRes.data.name;
        const slug = industrySlug || 'all-businesses';
        const path = industrySlug
          ? `/workers-comp-for-${industrySlug}-in-${citySlug}-ca`
          : `/workers-comp-insurance-${citySlug}-ca`;
        setCanonical(path);
        setMeta(
          `Workers Comp Insurance for ${indName} in ${cityName}, CA | CompareWorkersComp.com`,
          `Compare workers compensation insurance quotes for ${indName.toLowerCase()} in ${cityName}, California. Free quotes from 50+ carriers.`
        );
        const [authRes, faqRes, tipRes, paaRes] = await Promise.all([
          api.get(`/authority/${slug}?city=${cityName}`),
          api.get(`/faqs/${slug}?city=${cityName}`),
          api.get('/tip-of-the-week'),
          api.get(`/paa/${slug}?city=${cityName}`)
        ]);
        setAuthority(authRes.data.content);
        setFaqs(faqRes.data.faqs);
        setTip(tipRes.data.tip);
        setPaa(paaRes.data.questions || []);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [citySlug, industrySlug, indName]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-cwc-gold border-t-transparent rounded-full" /></div>;
  if (!city) return <div className="min-h-screen flex items-center justify-center text-gray-500">City not found</div>;

  const cityName = city.name;
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'California', url: '/workers-comp-insurance-california' },
    { name: `${cityName}, CA`, url: `/workers-comp-insurance-${citySlug}-ca` }
  ];
  if (industrySlug && ind) breadcrumbs.push({ name: ind.name });

  const wcirbCodes = ind ? (ind.slug === 'construction' ? '5645, 5436, 5201' : ind.slug === 'restaurants' ? '9082, 9083' : ind.slug === 'home-health' ? '8835, 8827' : ind.slug === 'landscaping' ? '0042, 0050' : ind.slug === 'trucking' ? '7231, 7229' : 'Varies by classification') : 'Varies by classification';

  const fastFacts = [
    { icon: <AlertTriangle className="w-5 h-5 text-red-500" />, text: 'CA requires WC for every employer with 1+ employee' },
    { icon: <DollarSign className="w-5 h-5 text-green-500" />, text: `Avg cost: ${ind?.avgCost || '$1-$15 per $100 payroll'}` },
    { icon: <MapPin className="w-5 h-5 text-cwc-gold" />, text: `Key WCIRB codes: ${wcirbCodes}` }
  ];

  return (
    <>
      <ServiceSchema name={`Workers Comp for ${indName} in ${cityName}, CA`} description={`Compare workers compensation insurance quotes for ${indName.toLowerCase()} in ${cityName}, California`} areaServed={`${cityName}, California`} />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={breadcrumbs} />
      <SpeakableSchema name={`Workers Comp for ${indName} in ${cityName}`} url={`https://compareworkerscomp.com/workers-comp-insurance-${citySlug}-ca`} />

      {/* Hero with picture/video background */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/40 mb-6 flex-wrap" data-testid="breadcrumb">
            {breadcrumbs.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span>/</span>}
                {b.url ? <Link to={b.url} className="hover:text-white/70 transition-colors">{b.name}</Link> : <span className="text-white/70">{b.name}</span>}
              </span>
            ))}
          </nav>
          <p className="speakable-intro text-white/30 text-xs mb-3" data-testid="city-speakable">
            CompareWorkersComp.com is a workers compensation insurance comparison resource that helps California business owners find the right coverage for their industry.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6 hero-text-shadow" data-testid="city-h1">
                Workers Comp Insurance for {indName} in <span className="text-red-500">{cityName}, CA</span>
              </h1>
              {/* Fast Facts */}
              <div className="space-y-3 mb-6" data-testid="fast-facts">
                {fastFacts.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-3 border border-white/10">
                    {f.icon}
                    <span className="text-white/80 text-sm">{f.text}</span>
                  </div>
                ))}
              </div>
              <a href="#city-lead-form" onClick={() => { if (window.gtag) window.gtag('event', 'renewal_cta_click', { event_category: 'engagement', event_label: 'city_page' }); }} className="renewal-pulse inline-flex items-center gap-2 bg-green-500/20 backdrop-blur-sm text-white px-4 py-2.5 rounded-full border-2 border-green-400/40 hover:bg-green-500/30 transition-colors text-sm font-medium mt-2" data-testid="city-renewal-cta">
                Rates Change — Compare At Renewal
              </a>
            </div>
            <div id="city-lead-form" data-testid="city-lead-form">
              <LeadForm city={cityName} industry={industrySlug || ''} source="city-page" />
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="bg-gray-50 py-12" data-testid="city-reviews">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <GoogleReviews compact />
        </div>
      </section>

      {/* Authority Section */}
      {authority && (
        <section className="bg-white py-16" data-testid="authority-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-cwc-navy mb-6">What {cityName} {indName} Need to Know About Workers Comp</h2>
            <p className="text-gray-600 leading-relaxed">{authority}</p>
          </div>
        </section>
      )}

      {/* Industries in this City */}
      <section className="bg-gray-50 py-16" data-testid="city-industries">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-cwc-navy mb-3">Workers Comp by Industry in {cityName}, CA</h2>
          <p className="text-gray-500 text-sm mb-8">Find industry-specific workers comp information, WCIRB class codes, and quotes for {cityName}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map(i => {
              const isActive = industrySlug === i.slug;
              return (
                <Link key={i.slug} to={`/workers-comp-for-${i.slug}-in-${citySlug}-ca`} className={`rounded-xl overflow-hidden border transition-all group ${isActive ? 'border-red-300 ring-2 ring-red-200' : 'border-gray-100 hover:border-red-200 hover:shadow-md'}`} data-testid={`city-ind-${i.slug}`}>
                  <div className="h-32 overflow-hidden">
                    <img src={INDUSTRY_IMAGES[i.slug]} alt={`${i.name} workers`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className={`p-4 ${isActive ? 'bg-red-50' : 'bg-white'}`}>
                    <h3 className={`font-bold text-sm mb-1 ${isActive ? 'text-red-600' : 'text-cwc-navy group-hover:text-red-600'} transition-colors`}>{i.name}</h3>
                    <p className="text-gray-400 text-xs mb-2">{i.avgCost}</p>
                    <span className="text-red-600 text-xs font-medium">{isActive ? 'Currently viewing' : `View in ${cityName} →`}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* People Also Ask — H3+p format for Google PAA boxes */}
      {paa.length > 0 && (
        <section className="bg-gray-50 py-12" data-testid="city-paa">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-cwc-navy mb-6">People Also Ask About {cityName} Workers Comp</h2>
            <div className="space-y-6">
              {paa.map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-gray-100" data-testid={`paa-item-${i}`}>
                  <h3 className="font-semibold text-cwc-navy text-base mb-2">{item.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Workers' Comp Tip of the Week */}
      {tip && (
        <section className="bg-white py-12" data-testid="city-tip-of-week">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-green-50 rounded-2xl p-6 border border-green-200">
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                <Lightbulb className="w-3.5 h-3.5" /> Workers' Comp Tip of the Week
              </div>
              <p className="text-green-900 text-base leading-relaxed font-medium">{tip}</p>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="bg-gray-50 py-16" data-testid="city-faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-cwc-navy mb-8">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left font-medium text-cwc-navy hover:bg-gray-50">
                    <span className="pr-4">{faq.q}</span>
                    {openFaq === i ? <ChevronUp className="w-5 h-5 text-cwc-gold shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />}
                  </button>
                  {openFaq === i && <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Nearby Cities */}
      {city.nearby_cities && city.nearby_cities.length > 0 && (
        <section className="bg-white py-16" data-testid="nearby-cities">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-cwc-navy mb-6">Workers Comp in Nearby Cities</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {city.nearby_cities.map(nc => (
                <Link key={nc.slug} to={industrySlug ? `/workers-comp-for-${industrySlug}-in-${nc.slug}-ca` : `/workers-comp-insurance-${nc.slug}-ca`} className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 hover:bg-cwc-gold/10 transition-colors text-sm text-cwc-navy font-medium" data-testid={`nearby-${nc.slug}`}>
                  <MapPin className="w-4 h-4 text-cwc-gold shrink-0" /> {nc.name}, CA
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-cwc-navy py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Get Your Free {indName} Workers Comp Quote in {cityName}</h2>
          <p className="text-white/60 mb-6">Compare rates from 50+ carriers. No obligation.</p>
          <a href="#lead-form" className="inline-flex bg-red-600 text-white font-bold px-8 py-3 rounded-full hover:bg-red-700 transition-colors" data-testid="city-cta">Compare Free Quotes</a>
        </div>
      </section>

      {/* Carriers */}
      <section className="bg-gray-50 py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-3">Our Partners &mdash; Trusted Insurance Carriers</p>
          <img src="/carriers.png" alt="Insurance Carriers" className="max-w-full mx-auto opacity-80" loading="lazy" />
        </div>
      </section>

      {/* Featured In */}
      <section className="bg-white py-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-medium mb-3">Trio Insurance Featured In</p>
          <img src="/featured-in.png" alt="Featured in FOX, Google News, Digital Journal" className="max-w-xs mx-auto" loading="lazy" />
        </div>
      </section>
    </>
  );
}
